import React, { useContext } from 'react'
import { X, Pencil, FileText } from 'lucide-react'
import ModalContext from '../../contexts/ModalContext'
import UserContext, { Roles } from '../../contexts/UserContext'
import { useProfileContext } from '../../contexts/AccessesContext'
import { UserStatus } from '../../types/userStatus'
import {
  Container,
  GradientHeader,
  HeaderTop,
  CloseButton,
  AvatarWrap,
  Avatar,
  Body,
  NameRow,
  Name,
  Email,
  EditButton,
  BadgesRow,
  StatusBadge,
  ProfileBadge,
  Divider,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  SkillsBox,
  SkillsBoxLabel,
  ChipsWrap,
  Chip,
  EmptySkills,
  SectionTitle,
  AttachmentsList,
  AttachmentRow,
  AttachmentIcon,
  AttachmentName,
  AttachmentLink,
  Footer,
  CloseTextButton,
} from './style'

const ViewAccessUserModal: React.FC = () => {
  const { setModalActive, setModalSection, selectedAccessUser } = useContext(ModalContext)
  const { user } = useContext(UserContext)
  const { profiles } = useProfileContext()

  if (!selectedAccessUser) return null

  const isTenantAdmin = user?.role === Roles.tenantAdmin
  const isActive = selectedAccessUser.status === UserStatus.ACTIVE

  const initials = selectedAccessUser.name
    .split(' ')
    .slice(0, 2)
    .map((w: string) => w[0] ?? '')
    .join('')
    .toUpperCase()

  const profileName = profiles.find(p => p.id === selectedAccessUser.profileId)?.name ?? null
  const skills = selectedAccessUser.skills ?? []
  const attachments = selectedAccessUser.attachments ?? []

  const handleEdit = () => {
    setModalSection('editAccessUser')
  }

  return (
    <Container>
      <GradientHeader>
        <HeaderTop>
          <CloseButton onClick={() => setModalActive(false)}>
            <X size={14} />
          </CloseButton>
        </HeaderTop>
        <AvatarWrap>
          <Avatar>{initials}</Avatar>
        </AvatarWrap>
      </GradientHeader>

      <Body>
        <NameRow>
          <div>
            <Name>{selectedAccessUser.name}</Name>
            <Email>{selectedAccessUser.email}</Email>
          </div>
          {isTenantAdmin && (
            <EditButton onClick={handleEdit}>
              <Pencil size={12} />
              Editar
            </EditButton>
          )}
        </NameRow>

        <BadgesRow>
          <StatusBadge $active={isActive}>
            {isActive ? '● Ativo' : '● Inativo'}
          </StatusBadge>
          {profileName && <ProfileBadge>{profileName}</ProfileBadge>}
        </BadgesRow>

        <Divider />

        <InfoGrid>
          <InfoItem>
            <InfoLabel>Empresa</InfoLabel>
            <InfoValue>{selectedAccessUser.company || '—'}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Telefone</InfoLabel>
            <InfoValue>{selectedAccessUser.phone || '—'}</InfoValue>
          </InfoItem>
        </InfoGrid>

        <SkillsBox>
          <SkillsBoxLabel>Habilidades</SkillsBoxLabel>
          {skills.length > 0 ? (
            <ChipsWrap>
              {skills.map(s => (
                <Chip key={s.id}>{s.name}</Chip>
              ))}
            </ChipsWrap>
          ) : (
            <EmptySkills>Nenhuma habilidade cadastrada</EmptySkills>
          )}
        </SkillsBox>

        {attachments.length > 0 && (
          <>
            <Divider />
            <SectionTitle>Anexos</SectionTitle>
            <AttachmentsList>
              {attachments.map(a => (
                <AttachmentRow key={a.id}>
                  <AttachmentIcon>
                    <FileText size={14} />
                  </AttachmentIcon>
                  <AttachmentName>{a.title}</AttachmentName>
                  <AttachmentLink href={a.fileUrl} target="_blank" rel="noreferrer">
                    Download
                  </AttachmentLink>
                </AttachmentRow>
              ))}
            </AttachmentsList>
          </>
        )}

        <Footer>
          <CloseTextButton onClick={() => setModalActive(false)}>Fechar</CloseTextButton>
        </Footer>
      </Body>
    </Container>
  )
}

export default ViewAccessUserModal
